import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { sniffMime } from "@/lib/audio/mime";
import type { Playable } from "@/lib/audio/playable";
import { PLAYBACK_SEQUENCE_GAP_MS, PLAYBACK_WARMUP_MS } from "@/lib/constants";
import {
  type AudioAssetName,
  getAudioAssetSources,
} from "@/lib/data/audio-assets";

// Bluetooth headphones clip the start of a sound unless a short silence wakes their codec first.
let _ctx: AudioContext | null = null;
const getAudioContext = (): AudioContext => {
  if (!_ctx) _ctx = new AudioContext();
  return _ctx;
};

const TIMEOUT_MS = 200;

const playSilence = async (ms = PLAYBACK_WARMUP_MS) => {
  const ctx = getAudioContext();
  if (ctx.state === "suspended")
    await Promise.race([
      ctx.resume(),
      new Promise((r) => setTimeout(r, TIMEOUT_MS)),
    ]);
  if (ctx.state !== "running") return;
  const length = Math.ceil((ms / 1000) * ctx.sampleRate);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start();
  await Promise.race([
    new Promise<void>((resolve) => {
      source.onended = () => resolve();
    }),
    new Promise<void>((r) => setTimeout(r, ms + TIMEOUT_MS)),
  ]);
};

const listeners = new Set<() => void>();
let globalAudio: HTMLAudioElement | null = null;
let globalObjectUrl: string | null = null;
let currentlyPlayingSound: Playable | null = null;
let currentlyPlayingSequence: Playable[] | null = null;
let playGeneration = 0;
const audioAssetCache = new Map<AudioAssetName, HTMLAudioElement>();

export type PlaySoundAction = (
  onEnded?: () => void,
  onStarted?: (audio: HTMLAudioElement) => void,
) => void;

const notify = () => {
  for (const cb of listeners) cb();
};

const getAudioAsset = (name: AudioAssetName): HTMLAudioElement => {
  const cachedAudio = audioAssetCache.get(name);
  if (cachedAudio) return cachedAudio;

  const audio = new Audio();
  audio.preload = "auto";
  for (const bundledSource of getAudioAssetSources(name)) {
    const source = document.createElement("source");
    source.src = bundledSource.url;
    source.type = bundledSource.type;
    audio.append(source);
  }
  audio.addEventListener(
    "error",
    () => {
      if (audioAssetCache.get(name) === audio) audioAssetCache.delete(name);
    },
    { once: true },
  );
  audioAssetCache.set(name, audio);
  audio.load();
  return audio;
};

const createAudio = (
  sound: Playable,
): { audio: HTMLAudioElement; objectUrl: string | null } => {
  if (sound.kind === "audioAsset") {
    return { audio: getAudioAsset(sound.data), objectUrl: null };
  }

  const audio = new Audio();
  audio.preload = "auto";

  const binary = atob(sound.data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const objectUrl = URL.createObjectURL(
    new Blob([bytes], { type: sniffMime(bytes) }),
  );
  audio.src = objectUrl;
  return { audio, objectUrl };
};

const revokeObjectUrl = (objectUrl: string | null) => {
  if (!objectUrl) return;
  URL.revokeObjectURL(objectUrl);
  if (globalObjectUrl === objectUrl) globalObjectUrl = null;
};

const revokeGlobalObjectUrl = () => revokeObjectUrl(globalObjectUrl);

const playSound = async (
  sound: Playable,
  onEnded?: () => void,
  onStarted?: (audio: HTMLAudioElement) => void,
  skipSilence = false,
) => {
  if (globalAudio) {
    globalAudio.pause();
    revokeGlobalObjectUrl();
    globalAudio = null;
  }

  const gen = ++playGeneration;
  currentlyPlayingSound = sound;
  notify();

  if (!skipSilence) {
    await playSilence();
    if (gen !== playGeneration) {
      return;
    }
  }

  const { audio, objectUrl } = createAudio(sound);
  if (audio.currentTime !== 0) audio.currentTime = 0;
  globalObjectUrl = objectUrl;
  globalAudio = audio;

  audio.onended = () => {
    if (gen !== playGeneration) {
      revokeObjectUrl(objectUrl);
      return;
    }
    globalAudio = null;
    currentlyPlayingSound = null;
    notify();
    revokeObjectUrl(objectUrl);
    onEnded?.();
  };

  audio
    .play()
    .then(() => {
      if (gen === playGeneration) onStarted?.(audio);
    })
    .catch((err) => {
      console.error("Audio play failed:", err);
      if (gen !== playGeneration) {
        revokeObjectUrl(objectUrl);
        return;
      }
      globalAudio = null;
      currentlyPlayingSound = null;
      notify();
      revokeObjectUrl(objectUrl);
      onEnded?.();
    });
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};

const playableEqual = (a: Playable, b: Playable) =>
  a.kind === b.kind && a.data === b.data;

const playablesEqual = (a: Playable[], b: Playable[] | null): boolean => {
  if (!b) return false;
  if (a.length !== b.length) return false;
  return a.every((item, i) => {
    const bItem = b[i];
    return bItem && playableEqual(item, bItem);
  });
};

const containsPlayable = (arr: Playable[], p: Playable | null) =>
  p !== null && arr.some((item) => playableEqual(item, p));

const isSoundsPlaying = (sounds: Playable[]) => {
  if (sounds.length === 1)
    return containsPlayable(sounds, currentlyPlayingSound);
  return playablesEqual(sounds, currentlyPlayingSequence);
};

export type PlayablePlaybackState = {
  isPlaying: boolean;
  hasWarmup: boolean;
};

const PLAYABLE_IDLE_STATE: PlayablePlaybackState = {
  isPlaying: false,
  hasWarmup: false,
};

const PLAYABLE_PLAYING_WITH_WARMUP_STATE: PlayablePlaybackState = {
  isPlaying: true,
  hasWarmup: true,
};

const PLAYABLE_PLAYING_NO_WARMUP_STATE: PlayablePlaybackState = {
  isPlaying: true,
  hasWarmup: false,
};

const getPlayablePlaybackState = (sound: Playable): PlayablePlaybackState => {
  const isPlaying =
    currentlyPlayingSound !== null &&
    playableEqual(sound, currentlyPlayingSound);
  if (!isPlaying) {
    return PLAYABLE_IDLE_STATE;
  }

  const hasWarmup =
    currentlyPlayingSequence === null ||
    playableEqual(sound, currentlyPlayingSequence[0] as Playable);

  return hasWarmup
    ? PLAYABLE_PLAYING_WITH_WARMUP_STATE
    : PLAYABLE_PLAYING_NO_WARMUP_STATE;
};

const allAsAudioAssets = (names: readonly AudioAssetName[]): Playable[] =>
  names.map((data) => ({ kind: "audioAsset", data }));

const useStableSounds = (sounds: Playable[]) => {
  const ref = useRef(sounds);
  if (!playablesEqual(sounds, ref.current)) {
    ref.current = sounds;
  }
  return ref.current;
};

const usePreloadSounds = (sounds: Playable[]) => {
  useEffect(() => {
    for (const sound of sounds) {
      if (sound.kind === "audioAsset") getAudioAsset(sound.data);
    }
  }, [sounds]);
};

const usePlayCallback = (stableSounds: Playable[], msDelay: number) =>
  useCallback(
    (onEnded?: () => void, onStarted?: (audio: HTMLAudioElement) => void) => {
      if (stableSounds.length === 0) {
        onEnded?.();
        return;
      }

      currentlyPlayingSequence = stableSounds;
      notify();

      const playNext = (index: number) => {
        if (!playablesEqual(stableSounds, currentlyPlayingSequence)) return;
        const sound = stableSounds[index];
        if (!sound) {
          currentlyPlayingSequence = null;
          notify();
          onEnded?.();
          return;
        }
        playSound(
          sound,
          () => {
            const isLast = index === stableSounds.length - 1;
            if (isLast) {
              currentlyPlayingSequence = null;
              notify();
              onEnded?.();
              return;
            }
            setTimeout(() => playNext(index + 1), msDelay);
          },
          index === 0 ? onStarted : undefined,
          index > 0,
        );
      };

      playNext(0);
    },
    [stableSounds, msDelay],
  );

const usePlaySounds = (
  sounds: Playable[],
  msDelay = PLAYBACK_SEQUENCE_GAP_MS,
) => {
  const stableSounds = useStableSounds(sounds);
  usePreloadSounds(stableSounds);

  const playing = useSyncExternalStore(subscribe, () =>
    isSoundsPlaying(stableSounds),
  );

  const play = usePlayCallback(stableSounds, msDelay);

  return [play, playing] as const;
};

export const usePlaySoundsAction = (
  sounds: Playable[],
  msDelay = PLAYBACK_SEQUENCE_GAP_MS,
) => {
  const stableSounds = useStableSounds(sounds);
  usePreloadSounds(stableSounds);
  return usePlayCallback(stableSounds, msDelay);
};

export const usePlayablePlaybackState = (sound: Playable) =>
  useSyncExternalStore(subscribe, () => getPlayablePlaybackState(sound));

export const stopAllSounds = () => {
  playGeneration += 1;
  if (globalAudio) globalAudio.pause();
  revokeGlobalObjectUrl();
  globalAudio = null;
  currentlyPlayingSound = null;
  currentlyPlayingSequence = null;
  notify();
};

export const usePlayBundledSounds = (
  audioAssetNames: readonly AudioAssetName[],
  msDelay?: number,
) => usePlaySounds(allAsAudioAssets(audioAssetNames), msDelay);

export const usePlayBundledSoundsAction = (
  audioAssetNames: readonly AudioAssetName[],
  msDelay?: number,
) => usePlaySoundsAction(allAsAudioAssets(audioAssetNames), msDelay);
